import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  Stethoscope,
  ShieldCheck,
  Brain,
  Lock,
  Users,
  ClipboardList,
  Activity,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Pill,
  HeartPulse,
  LineChart,
  Leaf,
} from 'lucide-react';
import { BRAND_NAME } from '../utils/brand';
import WhatsAppCta from '../components/WhatsAppCta';
import { trackCtaClick } from '../utils/analytics';
import Reveal from '../components/Reveal';
import Marquee from '../components/Marquee';
import ScanRing from '../components/ScanRing';

//
// /weight-loss — MyDr.Ai Weight Loss Programme page.
// Source: Website Build Handoff V1.0, Appendix B (sections 4–29).
// Copy below is taken from the approved spec. DO NOT swap in marketing claims
// like "guaranteed weight loss", "instant approval", or specific kg amounts.
// CTAs route to /intake for now; will switch to /assessment in Phase D.
//

const ASSESSMENT_HREF = '/intake';
const CONSULT_HREF = '/contact';

// ────────────────────────────────────────────────────────────────────────────
// Small shared building blocks
// ────────────────────────────────────────────────────────────────────────────

function Section({ children, className = '', tone = 'white' }) {
  const bg =
    tone === 'tinted' ? 'bg-brand-50/40'
    : tone === 'navy' ? 'bg-ink-900 text-white'
    : 'bg-white';
  return (
    <section className={`${bg} ${className}`}>
      <div className="container-x py-16 sm:py-20">
        <Reveal>{children}</Reveal>
      </div>
    </section>
  );
}

function Eyebrow({ children }) {
  return (
    <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-[11px] font-semibold tracking-[0.18em] uppercase bg-brand-50 text-brand-700 border border-brand-100">
      {children}
    </span>
  );
}

function H2({ children, className = '' }) {
  return (
    <h2 className={`text-3xl sm:text-4xl font-bold tracking-tight text-ink-900 ${className}`}>
      {children}
    </h2>
  );
}

function PrimaryCta({ children = 'Start Your Free AI Assessment', to = ASSESSMENT_HREF, sourcePage = 'weight_loss' }) {
  return (
    <Link
      to={to}
      onClick={() => trackCtaClick({
        page: sourcePage,
        button_label: typeof children === 'string' ? children : 'Am I Qualified?',
        destination: to,
      })}
      className="inline-flex items-center gap-2 font-medium rounded-full px-6 py-3.5 text-base bg-brand-600 text-white hover:bg-brand-700 transition-colors shadow-card"
    >
      {children} <ArrowRight size={16} />
    </Link>
  );
}

function GhostCta({ children, to }) {
  return (
    <Link
      to={to}
      className="inline-flex items-center gap-2 font-medium rounded-full px-6 py-3.5 text-sm border border-ink-200 text-ink-900 hover:bg-ink-50 transition-colors"
    >
      {children}
    </Link>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// 1. Hero (Appendix B §4 — primary copy block)
// ────────────────────────────────────────────────────────────────────────────

function Hero() {
  return (
    <section className="relative isolate overflow-hidden bg-ink-900 text-white">
      <div
        aria-hidden="true"
        className="absolute inset-0 -z-10"
        style={{
          background: `
            radial-gradient(60% 50% at 78% 25%, rgba(14,178,171,0.30), transparent 65%),
            radial-gradient(45% 40% at 8% 90%, rgba(2,62,115,0.55), transparent 70%),
            linear-gradient(180deg, #02213F 0%, #023E73 100%)
          `,
        }}
      />
      <ScanRing className="absolute -top-1/3 right-[-12%] w-[44rem] h-[44rem] -z-10" tone="teal" />
      <div className="container-x py-20 sm:py-24 lg:py-28">
        <div className="max-w-3xl">
          <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-[11px] font-semibold tracking-[0.18em] uppercase bg-white/10 text-brand-200 border border-white/15">
            Doctor-led · AI-assisted · Personalised for Malaysians
          </span>

          <h1 className="mt-6 font-bold tracking-tight text-white text-5xl sm:text-6xl md:text-7xl leading-[1.04]">
            Lose Weight Smarter,
            <br />
            <span className="text-brand-300">Not Harder.</span>
          </h1>

          <p className="mt-6 text-lg sm:text-xl text-white/85 max-w-2xl">
            Doctor-led medical weight loss powered by AI technology and personalised
            healthcare guidance.
          </p>

          <p className="mt-4 text-base text-white/70 max-w-2xl">
            {BRAND_NAME} helps eligible adults in Malaysia access a safer, more
            structured approach to weight management through AI-assisted assessment,
            licensed healthcare provider consultation and ongoing digital support.
            No extreme diets. No guessing. No unregulated shortcuts. Just
            science-backed care designed around your body, your lifestyle and your
            health goals.
          </p>

          <ul className="mt-8 grid sm:grid-cols-2 gap-y-2 gap-x-6 max-w-xl text-sm text-white/85">
            <li className="flex items-center gap-2"><CheckCircle2 size={16} className="text-brand-300" /> AI-powered health assessment</li>
            <li className="flex items-center gap-2"><CheckCircle2 size={16} className="text-brand-300" /> Licensed healthcare provider review</li>
            <li className="flex items-center gap-2"><CheckCircle2 size={16} className="text-brand-300" /> Personalised weight management pathway</li>
            <li className="flex items-center gap-2"><CheckCircle2 size={16} className="text-brand-300" /> Ongoing progress tracking and support</li>
          </ul>

          <div className="mt-10 flex flex-wrap gap-3">
            <PrimaryCta />
            <Link
              to="/how-it-works"
              className="inline-flex items-center gap-2 font-medium rounded-full px-6 py-3.5 text-sm border border-white/25 text-white hover:bg-white/10 transition-colors"
            >
              Learn How It Works
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// 2. Trust Bar (Appendix B §5)
// ────────────────────────────────────────────────────────────────────────────

const TRUST_BADGES = [
  { icon: Stethoscope, label: 'Licensed Healthcare Provider Network' },
  { icon: Brain,       label: 'AI-Assisted Health Assessment' },
  { icon: ShieldCheck, label: 'Evidence-Based Medical Weight Management' },
  { icon: Lock,        label: 'Secure & PDPA-Conscious Platform' },
  { icon: Users,       label: 'Personalised for Malaysians' },
];

function TrustBar() {
  const marqueeItems = TRUST_BADGES.map(({ icon: Icon, label }) => (
    <span key={label} className="inline-flex items-center gap-2">
      <Icon size={14} className="text-brand-600" />
      <span className="text-ink-900">{label}</span>
    </span>
  ));
  return (
    <>
      <Marquee items={marqueeItems} bg="bg-white" />
      <section className="bg-canvas border-b border-ink-200">
        <div className="container-x py-10 text-center">
          <Reveal>
            <h2 className="text-lg sm:text-xl font-bold text-ink-900">
              Built for safe, structured care.
            </h2>
          </Reveal>
        </div>
      </section>
    </>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// 3. What Is Medical Weight Loss? (Appendix B §6)
// ────────────────────────────────────────────────────────────────────────────

function MedicalWeightLoss() {
  return (
    <Section>
      <div className="max-w-3xl">
        <Eyebrow>What Is Medical Weight Loss?</Eyebrow>
        <H2 className="mt-4">Medical weight loss is not another diet plan.</H2>
        <p className="mt-5 text-ink-600">
          Medical weight loss is a structured healthcare approach for people who may
          need more than generic diet advice. It considers your weight history, appetite
          patterns, medical conditions, medication use, lifestyle, metabolic risk
          factors and personal goals.
        </p>
        <p className="mt-4 text-ink-600">
          For eligible individuals, a doctor may consider evidence-based treatments
          that support appetite control, metabolic health and sustainable weight
          reduction. These treatments are used together with nutrition, physical
          activity, behaviour change and ongoing monitoring.
        </p>

        <ul className="mt-6 grid sm:grid-cols-2 gap-3 text-sm text-ink-900">
          {[
            'Designed around your health profile',
            'Reviewed by licensed healthcare providers',
            'Supported by lifestyle and behaviour guidance',
            'Monitored for safety, progress and suitability',
          ].map((t) => (
            <li key={t} className="flex items-start gap-2.5">
              <CheckCircle2 size={18} className="text-brand-600 mt-0.5 shrink-0" />
              {t}
            </li>
          ))}
        </ul>

        <div className="mt-8"><PrimaryCta>Start My Health Assessment</PrimaryCta></div>
      </div>
    </Section>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// 4. What Is Obesity? + BMI block (Appendix B §7)
// ────────────────────────────────────────────────────────────────────────────

function Obesity() {
  return (
    <Section tone="tinted">
      <div className="grid lg:grid-cols-2 gap-12 items-start">
        <div>
          <Eyebrow>What Is Obesity?</Eyebrow>
          <H2 className="mt-4">Obesity is a medical condition, not a character flaw.</H2>
          <p className="mt-5 text-ink-600">
            Obesity is commonly understood as excess body fat that may affect health.
            Body mass index, or BMI, is often used as a screening tool, but it is not
            the full picture. Waist circumference, medical history, blood pressure,
            glucose levels, cholesterol, sleep, medications and family history may
            also matter.
          </p>
          <p className="mt-4 text-ink-600">
            For many people, weight gain is influenced by biology: appetite hormones,
            insulin resistance, genetics, stress, sleep quality, medications and the
            modern food environment. That is why "just eat less" is often not enough.
          </p>

          <ul className="mt-6 space-y-2 text-sm text-ink-900">
            <li className="flex items-start gap-2.5"><CheckCircle2 size={18} className="text-brand-600 mt-0.5 shrink-0" />BMI is a screening tool, not a complete diagnosis</li>
            <li className="flex items-start gap-2.5"><CheckCircle2 size={18} className="text-brand-600 mt-0.5 shrink-0" />Health risks can vary between individuals with the same BMI</li>
            <li className="flex items-start gap-2.5"><CheckCircle2 size={18} className="text-brand-600 mt-0.5 shrink-0" />Weight management should be approached with medical context and compassion</li>
          </ul>
        </div>

        <div className="rounded-2xl bg-white border border-ink-200 shadow-soft overflow-hidden">
          <div className="px-6 py-4 bg-brand-600 text-white">
            <div className="text-xs uppercase tracking-[0.18em] font-semibold">BMI explainer</div>
            <div className="mt-1 font-bold">General adult BMI categories</div>
          </div>
          <table className="w-full text-sm">
            <tbody>
              {[
                ['Healthy weight', '18.5 to less than 25 kg/m²'],
                ['Overweight', '25 to less than 30 kg/m²'],
                ['Obesity', '30 kg/m² and above'],
              ].map(([cat, range]) => (
                <tr key={cat} className="border-b border-ink-100 last:border-0">
                  <td className="px-6 py-3 text-ink-900 font-medium">{cat}</td>
                  <td className="px-6 py-3 text-ink-600">{range}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="px-6 py-4 bg-brand-50/60 text-xs text-ink-700">
            <strong className="text-ink-900">Note for Malaysia:</strong> some Asian
            populations may experience metabolic risk at lower BMI levels. Doctor
            assessment is recommended. The BMI table above is educational; it should
            not be used to approve or reject treatment automatically without medical
            review.
          </div>
        </div>
      </div>
    </Section>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// 5. Why Losing Weight Can Be Difficult (Appendix B §8)
// ────────────────────────────────────────────────────────────────────────────

function WhyHard() {
  return (
    <Section>
      <div className="max-w-3xl">
        <Eyebrow>Why It Can Be Difficult</Eyebrow>
        <H2 className="mt-4">If weight loss feels harder than it should, you are not alone.</H2>
        <p className="mt-5 text-ink-600">
          Many people try diet after diet, lose weight for a short period, and then
          regain it. This can happen because the body adapts to weight loss by
          increasing hunger signals, reducing energy expenditure and pushing the body
          back toward its previous weight.
        </p>
        <p className="mt-4 text-ink-600">
          Your environment also matters. Long working hours, stress, irregular meals,
          poor sleep, family eating habits, social events and easy access to
          calorie-dense food can all make weight management harder. {BRAND_NAME} is
          built to support the full journey, not just the first consultation.
        </p>

        <div className="mt-8 grid sm:grid-cols-2 gap-3 text-sm">
          {[
            'Appetite can increase after dieting',
            'Metabolism may adapt during weight loss',
            'Stress and sleep can influence hunger and cravings',
            'Medical conditions and medications can affect weight',
            'Sustainable progress usually requires ongoing support',
          ].map((t) => (
            <div key={t} className="flex items-start gap-2.5 rounded-xl bg-canvas border border-ink-100 p-4">
              <Activity size={16} className="text-brand-600 mt-0.5 shrink-0" />
              <span className="text-ink-900">{t}</span>
            </div>
          ))}
        </div>

        <p className="mt-8 text-ink-600 italic">
          This is not about blame. It is about biology, behaviour and better support.
          You deserve respectful, evidence-based care.
        </p>
      </div>
    </Section>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// 6. Understanding GLP-1 Medications (Appendix B §9–10)
// ────────────────────────────────────────────────────────────────────────────

function GLP1() {
  return (
    <Section tone="tinted">
      <div className="max-w-3xl">
        <Eyebrow>Understanding GLP-1</Eyebrow>
        <H2 className="mt-4">GLP-1 medications are changing how medical weight loss is supported.</H2>
        <p className="mt-5 text-ink-600">
          GLP-1 receptor agonists are prescription medicines that act on pathways
          involved in blood sugar control, appetite and fullness. For certain eligible
          patients, GLP-1-based treatments may help reduce hunger, improve satiety
          and support weight reduction when used with a healthier eating pattern,
          physical activity and medical supervision.
        </p>
        <p className="mt-4 text-ink-600">
          These medicines are not suitable for everyone. They require proper medical
          assessment, safety screening, counselling and follow-up monitoring.
        </p>

        <ul className="mt-6 space-y-2 text-sm text-ink-900">
          {[
            'May help reduce appetite',
            'May help you feel full earlier and longer',
            'May support better eating consistency',
            'Must be prescribed only when medically appropriate',
            'Should be used with lifestyle and behaviour changes',
          ].map((t) => (
            <li key={t} className="flex items-start gap-2.5"><CheckCircle2 size={18} className="text-brand-600 mt-0.5 shrink-0" />{t}</li>
          ))}
        </ul>

        <div className="mt-8 rounded-2xl bg-white border border-ink-200 p-5 text-sm text-ink-700">
          It is not magic — it is medical support plus daily consistency. Medical
          weight loss can make the journey more manageable for suitable patients, but
          it still requires partnership between you, your doctor and your support
          system.
        </div>

        <div className="mt-8"><PrimaryCta>Find Out If I May Be Suitable</PrimaryCta></div>
      </div>
    </Section>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// 7. Programme Overview + How It Works (Appendix B §11–12)
// ────────────────────────────────────────────────────────────────────────────

const STEPS = [
  {
    n: '01',
    title: 'Complete your AI-assisted health assessment',
    text: 'Answer questions about your weight history, health conditions, medications, lifestyle, goals and preferences. This helps identify potential safety considerations.',
  },
  {
    n: '02',
    title: 'Eligibility pre-screening',
    text: 'Your responses help identify whether medical weight management may be suitable, or whether you should speak directly with a healthcare professional first.',
  },
  {
    n: '03',
    title: 'Doctor consultation',
    text: 'If appropriate, you will be guided to a consultation with a licensed healthcare provider who will review your history and determine suitability.',
  },
  {
    n: '04',
    title: 'Personalised plan',
    text: 'If suitable, your doctor may recommend a weight management plan — including lifestyle guidance, monitoring and medication where medically appropriate.',
  },
  {
    n: '05',
    title: 'Licensed pharmacy fulfilment',
    text: 'Where medication is prescribed, dispensing is arranged through licensed pharmacy partners according to applicable requirements.',
  },
  {
    n: '06',
    title: 'Progress tracking and follow-up support',
    text: `${BRAND_NAME} helps you stay on track with reminders, education, progress check-ins and follow-up prompts.`,
  },
];

function Process() {
  return (
    <Section>
      <div className="max-w-3xl">
        <Eyebrow>How the Programme Works</Eyebrow>
        <H2 className="mt-4">A complete pathway from assessment to follow-up support.</H2>
        <p className="mt-5 text-ink-600">
          The {BRAND_NAME} Weight Loss Programme is designed for adults in Malaysia
          who want a safer and more personalised approach to weight management. We
          combine AI-assisted intake, licensed medical consultation, personalised
          treatment planning and digital progress support. Where clinically
          appropriate, a doctor may discuss prescription treatment options.
        </p>
      </div>

      <ol className="mt-12 grid md:grid-cols-2 gap-5">
        {STEPS.map((s) => (
          <li key={s.n} className="rounded-2xl bg-white border border-ink-200 p-6 shadow-soft">
            <div className="flex items-baseline gap-3">
              <span className="text-3xl font-bold text-brand-600 tabular-nums">{s.n}</span>
              <h3 className="text-lg font-bold text-ink-900">{s.title}</h3>
            </div>
            <p className="mt-2 text-sm text-ink-600">{s.text}</p>
          </li>
        ))}
      </ol>

      <div className="mt-10"><PrimaryCta>Am I Qualified?</PrimaryCta></div>
    </Section>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// 8. What Is Included (Appendix B §13)
// ────────────────────────────────────────────────────────────────────────────

const INCLUDED = [
  'AI-assisted health and lifestyle assessment',
  'Eligibility pre-screening based on your responses',
  'Licensed healthcare provider consultation pathway',
  'Personalised weight management plan',
  'Prescription consideration where clinically appropriate',
  'Licensed pharmacy fulfilment where medication is prescribed',
  'Progress dashboard and weight tracking',
  'AI reminders and educational support',
  'Follow-up prompts and care coordination',
];

function Included() {
  return (
    <Section tone="tinted">
      <div className="max-w-3xl">
        <Eyebrow>What Is Included</Eyebrow>
        <H2 className="mt-4">Everything you need to begin safely and confidently.</H2>
      </div>
      <ul className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {INCLUDED.map((t) => (
          <li key={t} className="flex items-start gap-3 rounded-xl bg-white border border-ink-200 p-4">
            <CheckCircle2 size={18} className="text-brand-600 mt-0.5 shrink-0" />
            <span className="text-sm text-ink-900">{t}</span>
          </li>
        ))}
      </ul>
    </Section>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// 9. Who May / May Not Be Suitable (Appendix B §14–15)
// ────────────────────────────────────────────────────────────────────────────

function Suitability() {
  return (
    <Section>
      <div className="grid lg:grid-cols-2 gap-8">
        {/* May be suitable */}
        <div className="rounded-2xl border border-brand-100 bg-brand-50/30 p-7">
          <Eyebrow>Who May Be Suitable</Eyebrow>
          <h3 className="mt-4 text-2xl font-bold text-ink-900">
            Adults who want medical support for weight management.
          </h3>
          <p className="mt-3 text-ink-700">
            You may be a potential candidate if you are an adult in Malaysia and have
            struggled to lose weight through lifestyle changes alone, or if your
            weight is affecting your health, confidence, energy or daily life. Final
            suitability is determined only after medical review.
          </p>
          <ul className="mt-5 space-y-2 text-sm">
            {[
              'Adults with overweight or obesity concerns',
              'Tried diet and exercise but find it hard to sustain results',
              'Weight-related health risks (high blood pressure, abnormal cholesterol, insulin resistance, type 2 diabetes)',
              'Want structured support instead of unregulated slimming products',
              'Ready to make lifestyle changes alongside medical guidance',
            ].map((t) => (
              <li key={t} className="flex items-start gap-2.5 text-ink-900">
                <CheckCircle2 size={18} className="text-brand-600 mt-0.5 shrink-0" />
                {t}
              </li>
            ))}
          </ul>
          <div className="mt-6">
            <PrimaryCta>Check My Eligibility</PrimaryCta>
          </div>
        </div>

        {/* May not be suitable */}
        <div className="rounded-2xl border border-ink-200 bg-white p-7">
          <Eyebrow>Who May Not Be Suitable</Eyebrow>
          <h3 className="mt-4 text-2xl font-bold text-ink-900">
            Medical weight loss treatment is not suitable for everyone.
          </h3>
          <p className="mt-3 text-ink-700">
            A doctor will need to understand your full medical history before
            deciding whether any prescription treatment is appropriate. Some people
            may need further assessment, alternative treatment or referral to another
            healthcare professional.
          </p>
          <ul className="mt-5 space-y-2 text-sm">
            {[
              'Pregnant or planning pregnancy — speak with a doctor before treatment',
              'Breastfeeding — obtain medical advice before any medication',
              'History of certain thyroid cancers or endocrine conditions',
              'Pancreatitis history, severe GI disease, kidney or gallbladder issues, diabetic retinopathy, eating disorder history',
              'Scheduled for surgery or anaesthesia — inform your doctor about GLP-1 use',
              'Experiencing urgent symptoms — seek immediate medical care, do not rely on an online platform',
            ].map((t) => (
              <li key={t} className="flex items-start gap-2.5 text-ink-900">
                <XCircle size={18} className="text-ink-500 mt-0.5 shrink-0" />
                {t}
              </li>
            ))}
          </ul>
          <div className="mt-6">
            <GhostCta to={CONSULT_HREF}>Speak to a Healthcare Provider</GhostCta>
          </div>
        </div>
      </div>
    </Section>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// 10. Doctor Consultation Process (Appendix B §16)
// ────────────────────────────────────────────────────────────────────────────

function Doctor() {
  return (
    <Section tone="tinted">
      <div className="max-w-3xl">
        <Eyebrow>Doctor Consultation</Eyebrow>
        <H2 className="mt-4">Your treatment decision belongs with a licensed doctor.</H2>
        <p className="mt-5 text-ink-600">
          After your assessment, a licensed healthcare provider will review your
          information and, where appropriate, conduct a consultation. The doctor may
          ask about your medical history, current medications, previous weight loss
          attempts, family history, pregnancy plans, lifestyle, eating patterns and
          treatment goals.
        </p>
        <p className="mt-4 text-ink-600">
          The doctor will determine whether medical weight management is suitable
          for you and whether prescription medication should be considered.
        </p>

        <ul className="mt-6 grid sm:grid-cols-2 gap-2 text-sm text-ink-900">
          {[
            'Medical history review',
            'Medication and allergy review',
            'Weight and BMI assessment',
            'Risk factor screening',
            'Lifestyle and readiness discussion',
            'Explanation of potential benefits, risks and alternatives',
            'Prescription decision only if medically appropriate',
          ].map((t) => (
            <li key={t} className="flex items-start gap-2.5">
              <Stethoscope size={16} className="text-brand-600 mt-0.5 shrink-0" />
              {t}
            </li>
          ))}
        </ul>

        <div className="mt-8 flex flex-wrap gap-3">
          <PrimaryCta>Book Doctor Consultation</PrimaryCta>
          <GhostCta to={ASSESSMENT_HREF}>Start Privately</GhostCta>
        </div>

        <p className="mt-6 text-sm text-ink-600 italic">
          Private, respectful and judgement-free — your consultation is a safe space
          to discuss your concerns honestly.
        </p>
      </div>
    </Section>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// 11. Personalised Treatment Plan + Pharmacy Fulfilment (Appendix B §17–18)
// ────────────────────────────────────────────────────────────────────────────

function PlanAndPharmacy() {
  return (
    <Section>
      <div className="grid lg:grid-cols-2 gap-8">
        <div>
          <Eyebrow>Personalised Plan</Eyebrow>
          <H2 className="mt-4">Your plan should fit your body, not someone else's routine.</H2>
          <p className="mt-4 text-ink-600">
            If you are suitable for the programme, your treatment plan may include
            nutrition direction, activity goals, progress tracking, follow-up timing
            and, where medically appropriate, prescription treatment options. Your
            plan may be adjusted over time based on your progress, tolerance, side
            effects, safety signals and doctor recommendations.
          </p>
          <ul className="mt-5 space-y-2 text-sm text-ink-900">
            {[
              'Weight and progress goals',
              'Eating pattern and portion guidance',
              'Physical activity recommendations matched to your level',
              'Medication counselling where applicable',
              'Side-effect education and follow-up instructions',
              'Progress check-ins and plan adjustments',
            ].map((t) => (
              <li key={t} className="flex items-start gap-2.5">
                <HeartPulse size={16} className="text-brand-600 mt-0.5 shrink-0" />
                {t}
              </li>
            ))}
          </ul>
          <p className="mt-6 text-ink-600 italic">
            The goal is not crash weight loss. The goal is better long-term health.
          </p>
        </div>

        <div className="rounded-2xl border border-ink-200 bg-canvas p-7">
          <Eyebrow>Pharmacy Fulfilment</Eyebrow>
          <h3 className="mt-4 text-2xl font-bold text-ink-900">
            Medication is supplied only when legally prescribed and medically appropriate.
          </h3>
          <p className="mt-3 text-ink-700">
            {BRAND_NAME} does not sell medication without medical review. If a
            licensed doctor determines that prescription treatment is suitable for
            you, medication fulfilment is arranged through licensed pharmacy partners
            according to Malaysian requirements. This helps protect patients from
            counterfeit, unregistered or unsafe medication sources.
          </p>
          <ul className="mt-5 space-y-2 text-sm text-ink-900">
            {[
              'No prescription without doctor assessment',
              'No guaranteed approval',
              'Medication only where clinically appropriate',
              'Dispensing through licensed pharmacy partners',
              'Patient counselling and safety instructions where required',
            ].map((t) => (
              <li key={t} className="flex items-start gap-2.5">
                <Pill size={16} className="text-brand-600 mt-0.5 shrink-0" />
                {t}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </Section>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// 12. Expected Outcomes (Appendix B §19) — no kg promises (spec sec. 31)
// ────────────────────────────────────────────────────────────────────────────

function Outcomes() {
  return (
    <Section tone="tinted">
      <div className="max-w-3xl">
        <Eyebrow>Realistic Progress</Eyebrow>
        <H2 className="mt-4">Progress should be measured safely, not emotionally.</H2>
        <p className="mt-5 text-ink-600">
          Weight loss varies from person to person. Your result may depend on
          starting weight, medical conditions, medication suitability, adherence,
          nutrition, activity, sleep, stress, side effects and follow-up consistency.
          Some people may notice appetite changes early. Others may need more time,
          plan adjustments or alternative approaches. Your healthcare provider will
          help guide realistic expectations.
        </p>

        <ul className="mt-6 grid sm:grid-cols-2 gap-2 text-sm text-ink-900">
          {[
            'Individual results vary',
            'Progress is usually gradual',
            'Healthy habits still matter',
            'Follow-up helps improve safety and consistency',
            'Medication may not be suitable or effective for everyone',
          ].map((t) => (
            <li key={t} className="flex items-start gap-2.5">
              <LineChart size={16} className="text-brand-600 mt-0.5 shrink-0" />
              {t}
            </li>
          ))}
        </ul>

        <p className="mt-8 text-ink-700">
          <strong>Beyond the number on the scale.</strong> A healthier journey may
          include improvements in waist size, energy, cravings, portion control,
          blood pressure, glucose patterns, cholesterol markers, sleep, confidence
          and daily movement. Your doctor may recommend lab tests or additional
          monitoring when appropriate.
        </p>
      </div>
    </Section>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// 13. Safety + common side effects (Appendix B §20–21)
// ────────────────────────────────────────────────────────────────────────────

function Safety() {
  return (
    <Section>
      <div className="grid lg:grid-cols-2 gap-8">
        <div>
          <Eyebrow>Safety Information</Eyebrow>
          <H2 className="mt-4">Your safety comes first.</H2>
          <p className="mt-4 text-ink-600">
            Medical weight loss treatments can have side effects and are not
            suitable for everyone. Before starting any medication, your doctor
            should explain the expected benefits, possible risks, warning signs,
            alternatives and follow-up requirements. You should provide accurate
            information about your medical history, current medication, supplements,
            allergies, pregnancy plans, previous reactions and upcoming procedures.
          </p>
          <ul className="mt-5 space-y-2 text-sm text-ink-900">
            {[
              "Always follow your doctor's instructions",
              'Do not share prescription medication with anyone',
              'Do not buy prescription medication from unverified sources',
              'Inform your doctor if you experience severe or worrying symptoms',
              'Tell your healthcare provider about upcoming surgery, endoscopy or anaesthesia',
            ].map((t) => (
              <li key={t} className="flex items-start gap-2.5">
                <ShieldCheck size={16} className="text-brand-600 mt-0.5 shrink-0" />
                {t}
              </li>
            ))}
          </ul>

          <div className="mt-6 rounded-xl border border-amber-200 bg-amber-50/60 p-4 text-sm text-amber-900 flex gap-3">
            <AlertTriangle size={18} className="mt-0.5 shrink-0" />
            <div>
              <strong>When to seek urgent help.</strong> Seek immediate medical
              attention if you experience severe or persistent abdominal pain,
              repeated vomiting, signs of allergic reaction, breathing difficulty,
              fainting, yellowing of skin or eyes, severe dehydration, confusion,
              chest pain, or any symptom that feels serious or unusual.
            </div>
          </div>
        </div>

        <div className="rounded-2xl bg-canvas border border-ink-200 p-7">
          <Eyebrow>Common Side Effects</Eyebrow>
          <h3 className="mt-4 text-2xl font-bold text-ink-900">
            Side effects should be monitored, not ignored.
          </h3>
          <p className="mt-3 text-ink-700">
            Some GLP-1-based treatments may cause gastrointestinal symptoms,
            especially during dose changes. Your doctor or pharmacist can guide you
            on what is expected, what can be managed, and what requires medical
            attention.
          </p>
          <ul className="mt-5 grid grid-cols-2 gap-2 text-sm">
            {['Nausea','Vomiting','Diarrhoea','Constipation','Bloating','Reduced appetite','Reflux','Stomach discomfort'].map((t) => (
              <li key={t} className="rounded-lg bg-white border border-ink-100 px-3 py-2 text-ink-900">{t}</li>
            ))}
          </ul>
          <p className="mt-4 text-xs text-ink-500">
            This list is intentionally not exhaustive. Exact risks depend on the
            medication prescribed and your individual health profile.
          </p>
        </div>
      </div>
    </Section>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// 14. AI Support + Lifestyle (Appendix B §22–23)
// ────────────────────────────────────────────────────────────────────────────

function AIAndLifestyle() {
  return (
    <Section tone="tinted">
      <div className="grid lg:grid-cols-2 gap-8">
        <div>
          <Eyebrow>AI Support</Eyebrow>
          <H2 className="mt-4">Support should not stop after the consultation.</H2>
          <p className="mt-4 text-ink-600">
            {BRAND_NAME} provides AI-assisted support to help you stay informed,
            consistent and accountable between medical touchpoints. The platform
            helps you track progress, remember follow-ups, understand your plan and
            build healthier habits over time.
          </p>
          <p className="mt-3 text-ink-600">
            AI does not replace your doctor. It supports your journey by helping
            you stay organised and engaged. The AI assistant does not diagnose,
            prescribe, replace emergency care or override a doctor's judgement.
          </p>
          <ul className="mt-5 grid sm:grid-cols-2 gap-2 text-sm text-ink-900">
            {[
              'Progress dashboard',
              'Medication and follow-up reminders',
              'Lifestyle habit prompts',
              'Education about weight management',
              'Symptom and side-effect check-in prompts',
              'Questions to prepare for your next doctor review',
            ].map((t) => (
              <li key={t} className="flex items-start gap-2.5">
                <Brain size={16} className="text-brand-600 mt-0.5 shrink-0" />
                {t}
              </li>
            ))}
          </ul>
        </div>

        <div>
          <Eyebrow>Lifestyle Support</Eyebrow>
          <H2 className="mt-4">Medication may reduce hunger. Habits protect your progress.</H2>
          <p className="mt-4 text-ink-600">
            The most successful weight management journeys usually combine medical
            guidance with practical daily changes. {BRAND_NAME} encourages a
            balanced eating pattern, adequate protein, fibre-rich foods, safe
            physical activity, hydration, sleep improvement and realistic behaviour
            goals. You do not need a perfect routine. You need a routine you can
            repeat.
          </p>
          <ul className="mt-5 space-y-2 text-sm text-ink-900">
            {[
              'Simple meal structure guidance',
              'Protein and fibre awareness',
              'Portion and hunger cue education',
              'Movement goals matched to your ability',
              'Sleep and stress habit prompts',
              'Follow-up support to reduce relapse risk',
            ].map((t) => (
              <li key={t} className="flex items-start gap-2.5">
                <Leaf size={16} className="text-brand-600 mt-0.5 shrink-0" />
                {t}
              </li>
            ))}
          </ul>
          <p className="mt-5 text-ink-700 italic">
            Designed for real Malaysian life — nasi lemak breakfasts, mamak meals,
            family dinners, office lunches, festive seasons, late-night work,
            hawker food and social eating.
          </p>
        </div>
      </div>
    </Section>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// 15. Pricing transparency placeholder (Appendix B §24)
// ────────────────────────────────────────────────────────────────────────────

const PRICING_ROWS = [
  ['AI Assessment',     'Free to start your eligibility check.'],
  ['Doctor Consultation', 'Fee will be shown before booking — once partner availability is confirmed.'],
  ['Medication',        'Considered only when clinically appropriate. Costs depend on doctor assessment and pharmacy fulfilment.'],
  ['Pharmacy & Delivery', 'Handled by licensed pharmacy partners. Shown before confirmation.'],
  ['Refund Policy',     'See Terms & Conditions.'],
];

function Pricing() {
  return (
    <Section>
      <div className="max-w-3xl">
        <Eyebrow>Pricing Transparency</Eyebrow>
        <H2 className="mt-4">Clear pricing before you commit.</H2>
        <p className="mt-5 text-ink-600">
          {BRAND_NAME} is designed to provide transparent programme information
          before you begin. Pricing may vary depending on consultation requirements,
          treatment suitability, medication type, pharmacy fulfilment, follow-up
          needs and optional add-on services. You will be informed of applicable
          fees before confirming any paid service.
        </p>
        <ul className="mt-5 space-y-2 text-sm text-ink-900">
          {[
            'No hidden commitment before assessment',
            'Consultation and programme fees shown before payment',
            'Medication costs depend on doctor assessment and pharmacy fulfilment',
            'No prescription or medication is guaranteed',
          ].map((t) => (
            <li key={t} className="flex items-start gap-2.5">
              <CheckCircle2 size={18} className="text-brand-600 mt-0.5 shrink-0" />
              {t}
            </li>
          ))}
        </ul>
      </div>

      <div className="mt-10 rounded-2xl border border-ink-200 overflow-hidden bg-white">
        <table className="w-full text-sm">
          <thead className="bg-ink-900 text-white">
            <tr>
              <th className="text-left px-6 py-3 font-semibold w-1/3">Element</th>
              <th className="text-left px-6 py-3 font-semibold">Status</th>
            </tr>
          </thead>
          <tbody>
            {PRICING_ROWS.map(([k, v]) => (
              <tr key={k} className="border-b border-ink-100 last:border-0">
                <td className="px-6 py-3 text-ink-900 font-medium">{k}</td>
                <td className="px-6 py-3 text-ink-600">{v}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Section>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// 16. FAQ (Appendix B §26 — abridged, top compliance-critical questions only)
// ────────────────────────────────────────────────────────────────────────────

const FAQS = [
  {
    q: `What is ${'MyDr.Ai'}?`,
    a: `${'MyDr.Ai'} is a Malaysia-first healthcare technology platform that helps eligible adults access doctor-led, AI-assisted medical weight loss support through assessment, consultation, care coordination and follow-up tools.`,
  },
  {
    q: 'Is MyDr.Ai a clinic or pharmacy?',
    a: 'No. MyDr.Ai is a technology and care coordination platform. Medical consultations, prescriptions and treatment decisions are handled by independent licensed healthcare providers. Where medication is prescribed, fulfilment is handled by licensed pharmacy partners.',
  },
  {
    q: 'Can I get GLP-1 medication through MyDr.Ai?',
    a: 'You may be assessed for suitability. Medication is not guaranteed. A licensed doctor must determine whether prescription treatment is appropriate for your health profile.',
  },
  {
    q: 'Is the AI assessment enough to get medication?',
    a: 'No. The AI-assisted assessment is only an intake and support tool. It does not create a prescription and does not replace doctor review.',
  },
  {
    q: 'Are results guaranteed?',
    a: 'No. Results vary depending on medical suitability, starting weight, adherence, lifestyle, tolerance, follow-up and individual response.',
  },
  {
    q: 'Who may not be suitable?',
    a: 'People who are pregnant, planning pregnancy, breastfeeding, have certain thyroid or endocrine conditions, pancreatitis history, severe GI disease, kidney issues, gallbladder disease or other complex conditions may require special assessment or may not be suitable for certain treatments.',
  },
  {
    q: 'What should I do before surgery or endoscopy?',
    a: 'Tell your doctor, surgeon or anaesthetist if you are using or considering any GLP-1 medication, because delayed stomach emptying may affect procedures involving anaesthesia or deep sedation.',
  },
  {
    q: 'Does AI replace my doctor?',
    a: 'No. AI supports intake, education, reminders and tracking. Medical decisions are made by licensed healthcare professionals.',
  },
  {
    q: 'Is my information private?',
    a: 'MyDr.Ai handles personal data according to its Privacy Policy and applicable Malaysian personal data requirements. Sensitive health information is protected and used only for relevant healthcare and platform purposes.',
  },
];

function FAQ() {
  return (
    <Section tone="tinted">
      <div className="max-w-3xl">
        <Eyebrow>FAQ</Eyebrow>
        <H2 className="mt-4">Frequently asked questions.</H2>
      </div>
      <div className="mt-10 max-w-4xl">
        {FAQS.map(({ q, a }) => (
          <details
            key={q}
            className="group border-b border-ink-200 py-5"
          >
            <summary className="cursor-pointer list-none flex items-center justify-between gap-4">
              <span className="font-semibold text-ink-900">{q}</span>
              <span className="shrink-0 w-7 h-7 rounded-full bg-white border border-ink-200 flex items-center justify-center text-brand-600 group-open:rotate-45 transition-transform">
                +
              </span>
            </summary>
            <p className="mt-3 text-ink-600 text-sm leading-relaxed">{a}</p>
          </details>
        ))}
      </div>
    </Section>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// 17. Final CTA + Disclaimer (Appendix B §28 final flow + §29)
// ────────────────────────────────────────────────────────────────────────────

function FinalCta() {
  return (
    <section className="relative isolate overflow-hidden bg-ink-900 text-white">
      <ScanRing className="absolute -top-1/3 left-[-15%] w-[42rem] h-[42rem] -z-10" tone="teal" />
      <div className="container-x py-20 sm:py-24 text-center">
        <Reveal>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight">
            Ready to start a safer weight loss journey?
          </h2>
          <p className="mt-5 text-white/80 max-w-2xl mx-auto">
            Take the first step with a private AI-assisted assessment. Your answers
            help us understand whether {BRAND_NAME} may be suitable for your weight
            management journey.
          </p>
          <div className="mt-9 flex flex-wrap gap-3 justify-center">
            <PrimaryCta sourcePage="weight_loss_final" />
            <WhatsAppCta
              sourcePage="weight_loss_final"
              label={`Talk to ${BRAND_NAME} on WhatsApp`}
              variant="ghost"
              className="!border-white/25 !text-white hover:!bg-white/10"
            />
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function PageDisclaimer() {
  return (
    <section className="bg-canvas border-t border-ink-200">
      <div className="container-x py-10 text-xs text-ink-500 max-w-4xl leading-relaxed">
        <p>
          <strong className="text-ink-900">Medical Disclaimer.</strong> {BRAND_NAME}
          {' '}is a healthcare technology and care coordination platform.
          {' '}{BRAND_NAME} does not itself practise medicine, diagnose medical
          conditions, prescribe medication or dispense medication. Medical
          consultations, diagnoses, prescriptions and treatment decisions are made
          solely by licensed healthcare providers exercising independent clinical
          judgement. Prescription medication is supplied only where legally
          prescribed and medically appropriate. Results vary between individuals
          and cannot be guaranteed. If you have a medical emergency or serious
          symptoms, seek immediate medical attention.
        </p>
      </div>
    </section>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Page export — sets SEO title/description per Appendix B §2
// ────────────────────────────────────────────────────────────────────────────

export default function WeightLoss() {
  useEffect(() => {
    const prevTitle = document.title;
    document.title = `${BRAND_NAME} Weight Loss Programme | AI-Powered Medical Weight Loss Malaysia`;

    const desc =
      `Start your medical weight loss journey with ${BRAND_NAME}. Doctor-led, ` +
      'AI-assisted and science-backed care for eligible Malaysians, with ' +
      'personalised assessment, consultation and follow-up support.';
    let metaDesc = document.querySelector('meta[name="description"]');
    const prevDesc = metaDesc?.getAttribute('content');
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.setAttribute('name', 'description');
      document.head.appendChild(metaDesc);
    }
    metaDesc.setAttribute('content', desc);

    return () => {
      document.title = prevTitle;
      if (prevDesc != null) metaDesc.setAttribute('content', prevDesc);
    };
  }, []);

  return (
    <>
      <Hero />
      <TrustBar />
      <MedicalWeightLoss />
      <Obesity />
      <WhyHard />
      <GLP1 />
      <Process />
      <Included />
      <Suitability />
      <Doctor />
      <PlanAndPharmacy />
      <Outcomes />
      <Safety />
      <AIAndLifestyle />
      <Pricing />
      <FAQ />
      <FinalCta />
      <PageDisclaimer />
    </>
  );
}
